import React, { useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getTime } from '../../../../services/projects-services';
import { fetchFilteredProjects, fetchFilteredProjectsGroups } from '../../projects.utils';
import { fetchCompanies } from '../../../../services/companiesServices';
import Loader from '../../../../components/loader/loader';
import ProjectColumns from './ProjectColumns';
import classNames from 'classnames';
import Table from '../../../../components/table/table';
import CreateProjectModal from '../../create-project-modal/create-project-modal';
import FilterComponent from '../../../../components/filter/filter';
import { useGetProjectListFiltersData } from '../../../../components/filter/filter.utils';
import { parsedProjectsFilterData } from './projects.utils';
import { useAuth } from '../../../../context/AuthContext';
import SearchHeader from '../../../../components/search-header/search-header';
import { ProjectTableStyled } from './ProjectTable.styles';
import { useRateCard } from '../../../../hooks/useRateCards';
import { generateProjectsCSVData } from './csv';
import { generatePlanRolesCSVData } from './plan-roles-csv';

function Projects() {
  const { userPermissions } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [nextId, setNextId] = useState(null);
  const [filterState, setFilterState] = useState([
    {
      key: 'state',
      value: 'Active',
      color: '#E4F9DC',
      titleName: 'State',
    },
    {
      key: 'state',
      value: 'Opportunity',
      color: '#E4F9DC',
      titleName: 'State',
    },
    {
      key: 'state',
      value: 'Committed',
      color: '#E4F9DC',
      titleName: 'State',
    },
  ]);

  const navigate = useNavigate();

  const { options, isLoading: isFilterDataLoading } = useGetProjectListFiltersData();

  const { data: users, isLoading: isUsersListLoading } = useQuery({
    queryKey: ['users'],
    queryFn: getTime,
  });

  const {
    data: projects,
    refetch,
    isLoading: isProjectsLoading,
    isFetching,
  } = useQuery({
    queryKey: ['projects', filterState, searchQuery],
    queryFn: () =>
      fetchFilteredProjects({
        filterState,
        searchQuery,
      }),
    refetchOnWindowFocus: false,
  });

  const { data: projectGroups } = useQuery({
    queryKey: ['project-groups', filterState],
    queryFn: () => fetchFilteredProjectsGroups(filterState),
    select: (groups) => {
      const groupMap = {};
      groups.forEach((group) => {
        groupMap[group.id] = group;
      });
      return groupMap;
    },
  });

  const { data: companiesList, isLoading: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: fetchCompanies,
  });

  const companies = useMemo(() => {
    const companyMap = {};
    companiesList?.forEach((company) => {
      companyMap[company.companyID] = company.companyName;
    });
    return companyMap;
  }, [companiesList]);

  const { data: rateCards = [] } = useRateCard();

  const searchFilteredProjects = useMemo(() => {
    const query = searchQuery?.toLowerCase().trim() || '';

    return (
      projects?.filter((project) => {
        const clientName = companies?.[project.client]?.toLowerCase() || '';
        const projectGroupName =
          projectGroups?.[project.addToExistingProject]?.projectName?.toLowerCase() || '';

        return (
          !query ||
          Object.values(project).some((value) => value?.toString().toLowerCase().includes(query)) ||
          clientName.includes(query) ||
          projectGroupName.includes(query)
        );
      }) || []
    );
  }, [projects, searchQuery, companies, projectGroups]);

  const tableData = useMemo(
    () =>
      parsedProjectsFilterData(
        searchFilteredProjects,
        // searchFilteredProjects?.filter((p) => p?.id == 20527),
        users,
        rateCards,
        companiesList,
      ),
    [searchFilteredProjects, users, rateCards, companiesList],
  );

  const handleSearch = debounce((query) => setSearchQuery(query.trim()), 300);

  const columns = useMemo(
    () => ProjectColumns(navigate, companies, projectGroups),
    [companies, navigate, projectGroups],
  );

  const showHeaderCta = useMemo(
    () =>
      userPermissions === 'Admin' ||
      userPermissions === 'Project Manager' ||
      userPermissions === 'Supervisor',
    [userPermissions],
  );

  const downloadProjectsCsv = async () => {
    const csv = await generateProjectsCSVData(tableData, projectGroups, companies);

    if (!csv) {
      alert('No data available to export.');
      return;
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'projects-list.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPlanRolesCsv = () => {
    const csv = generatePlanRolesCSVData(searchFilteredProjects || []);

    if (!csv) {
      alert('No project plans available to export.');
      return;
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'project-plan-role-report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const headerOptions = [
    {
      title: `Export List CSV`,
      onClick: () => {
        downloadProjectsCsv();
      },
    },
    {
      title: `Export Project Plan Report`,
      onClick: () => {
        downloadPlanRolesCsv();
      },
    },
  ];

  console.log(
    'tableData',
    tableData?.filter((p) => p?.id == 20935),
  );
  return (
    <ProjectTableStyled>
      <SearchHeader
        heading={'Projects'}
        placeholder={'Search a keyword, client or project.'}
        ctaTitle={showHeaderCta ? 'Add Project' : ''}
        ctaHandler={() => setShowModal(true)}
        handleSearch={handleSearch}
        optionsList={headerOptions}
        menuWidth={200}
      />
      <div className="projects-table">
        <FilterComponent
          label="Filter Projects"
          options={options}
          state={filterState}
          setState={setFilterState}
          isLoading={isFilterDataLoading}
          onClearFilters={() => setFilterState([])}
        />
        <>
          {!Array.isArray(projects) && !(isProjectsLoading || isFetching) && (
            <div>Invalid projects data</div>
          )}
          {(isUsersListLoading || isCompaniesLoading || isProjectsLoading || isFetching) && (
            <Loader absolute />
          )}

          {!isProjectsLoading && !isFetching && (
            <div
              className={classNames({
                'table-wrapper': true,
                'full-height': tableData.length > 0,
              })}
            >
              <Table
                data={tableData}
                columns={columns}
                isLoading={isUsersListLoading}
                noDataMessage={'No projects available'}
                sortable
                stickyHeader={true}
                rowBackground="white"
                rowHover={true}
                useUniformBackground={true}
              />

              {showModal && (
                <CreateProjectModal
                  nextId={nextId}
                  onClose={() => setShowModal(false)}
                  onSuccess={() => {
                    refetch();
                    setShowModal(false);
                  }}
                  isConfirming={false}
                  projectGroupOptions={projectGroups}
                  isCreationGroupView={false}
                />
              )}
            </div>
          )}
        </>
      </div>
    </ProjectTableStyled>
  );
}

export default React.memo(Projects);
